import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Router } from '@angular/router';
import { TrpcService } from './trpc.service';

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'tenant' | 'admin' | 'accounting';
}

export interface LoginCredentials {
  username: string;
  password: string;
}

export interface ChangePasswordRequest {
  token: string,
  new_password: string,
  confirm_new_password: string,
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
  public redirectUrl = '';

  constructor(
    private trpcService: TrpcService,
    private router: Router
  ) {
    this.checkStoredAuth();
  }

  private checkStoredAuth() {
    const token = localStorage.getItem('auth_token');
    const user = localStorage.getItem('current_user');
    if (token && user) {
      this.currentUserSubject.next(JSON.parse(user));
      this.isAuthenticatedSubject.next(true);
    }
  }

  async login(credentials: LoginCredentials): Promise<{ success: boolean; error?: string }> {
    try {
      const result = await this.trpcService.auth.login.mutate(credentials);

      console.log('Auth result from tRPC:', result);

      // The tRPC response is { user: {...}, token: "..." } directly
      if (result && result.user && result.token) {
        localStorage.setItem('auth_token', result.token);
        localStorage.setItem('current_user', JSON.stringify(result.user));
        this.currentUserSubject.next(result.user);
        this.isAuthenticatedSubject.next(true);

        if(this.redirectUrl) {
          this.router.navigateByUrl(this.redirectUrl);
        } else {
          // Redirect based on user role
          this.redirectAfterLogin(result.user.role);
        }

        return { success: true };
      } else {
        console.error('Unexpected login response format:', result);
        return { success: false, error: 'Invalid response format' };
      }
    } catch (error: any) {
      console.error('Login error:', error);
      return { success: false, error: error.message || 'Network error' };
    }
  }

  async forgotPassword(email: string): Promise<{ success: boolean; error?: string }> {
    try {
      const result = await this.trpcService.auth.forgotPassword.query(email);

      return { success: true };
    } catch (error: any) {
      console.error('Login error:', error);
      return { success: false, error: error.message || 'Network error' };
    }
  }

  async validateResetPasswordToken(token: string): Promise<{ success: boolean; error?: string }> {
    try {
      const result = await this.trpcService.auth.validateResetPasswordToken.query(token);
      if(!result) {
        throw new Error('invalid or expired token.');
      }
      return { success: true };
    } catch (error: any) {
      console.error('Login error:', error);
      return { success: false, error: error.message || 'Network error' };
    }
  }

  async resetPassword(params: ChangePasswordRequest): Promise<{ success: boolean; error?: string }> {
    try {
      await this.trpcService.auth.resetPassword.mutate(params);
      return { success: true };
    } catch (error: any) {
      console.error('Login error:', error);
      return { success: false, error: error.message || 'Network error' };
    }
  }

  async getSuites(): Promise<{ success: boolean; error?: string, suites?: any[] }> {
    try {
      const result = await this.trpcService.auth.getSuites.query();

      return { success: true, suites: result };
    } catch (error: any) {
      console.error('Login error:', error);
      return { success: false, error: error.message || 'Network error' };
    }
  }

  async validateUsername(params: any): Promise<{ success: boolean; error?: string }> {
    try {
      const result = await this.trpcService.auth.validateUsername.query(params);

      return { success: result };

    } catch (error: any) {
      return { success: false, error: error.message || 'Network error' };
    }
  }

  private redirectAfterLogin(role: string) {
    switch (role) {
      case 'tenant':
        this.router.navigate(['/tenant/dashboard']);
        break;
      case 'admin':
        this.router.navigate(['/admin/dashboard']);
        break;
      case 'accounting':
        this.router.navigate(['/accounting/dashboard']);
        break;
      default:
        this.router.navigate(['/dashboard']);
        break;
    }
  }

  logout() {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('current_user');
    this.currentUserSubject.next(null);
    this.isAuthenticatedSubject.next(false);
    this.router.navigate(['/login']);
  }

  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  isAuthenticated(): boolean {
    return this.isAuthenticatedSubject.value;
  }

  hasRole(role: string): boolean {
    const user = this.getCurrentUser();
    return user?.role === role;
  }
}